/**
 * A busca por telefone da lista de conversas (chamado de 21/10/2026, P8).
 *
 * A chave que ela compara é a de `@/telefone/chave-do-telefone` — a cópia presa a
 * `crm-backend/compartilhado/telefone.py`, que mora em `src/telefone/` desde a 13.2. O que fica
 * aqui é só o que é da **busca**: preparar o termo e decidir se um número casa com ele.
 *
 * **A busca é aditiva.** `prepararTermo` devolve `null` para o que não tem cara de telefone (tem
 * letra, ou menos de 4 dígitos), e aí a lista só faz o casamento de sempre (nome, número literal,
 * prévia). O mínimo de 4 dígitos existe para que `(22)` não vire `22` e puxe a lista inteira; um
 * termo curto sem máscara (`099`, `55`) continua casando pelo literal.
 *
 * **Os zeros à esquerda saem só aqui, na busca**, e nunca dentro de `chaveDoTelefone`: o `0` de
 * tronco é coisa de quem disca, e não do número.
 */

import {
  DDI_DO_BRASIL,
  INDICE_DO_NONO,
  TAMANHO_BRASILEIRO_COM_O_NONO,
  TAMANHO_BRASILEIRO_SEM_O_NONO,
  chaveDoTelefone,
  digitosDe,
} from '@/telefone/chave-do-telefone'

/** Abaixo disto o termo não é telefone: `(22)` puxaria quase a lista inteira. */
const MINIMO_DE_DIGITOS_NO_TERMO = 4

/** O que a busca prepara **uma** vez por termo, fora do `filter`. */
export interface TermoPreparado {
  /** As formas do termo que, contidas no número, casam: a crua, a sem zeros e a sem o nono. */
  candidatas: readonly string[]
  /** A chave do termo (já sem os zeros de discagem), para o casamento por chave igual. */
  chave: string | null
}

/**
 * Os zeros de discagem fora: o `0` de tronco quando sobram 10 ou 11 dígitos, e o `0` com os dois
 * da operadora quando sobram 12 ou 13 depois do `0` (`0 21 (22) 99755-0997`).
 */
function semZerosDeDiscagem(digitos: string): string {
  if (!digitos.startsWith('0')) {
    return digitos
  }
  const resto = digitos.slice(1)
  if (resto.length === 10 || resto.length === 11) {
    return resto
  }
  if (resto.length === 12 || resto.length === 13) {
    return resto.slice(2)
  }
  return digitos
}

/** O nono tirado do começo do pedaço local (`99423` → `9423`): de 5 a 9 dígitos. */
const MINIMO_DO_LOCAL_COM_O_NONO = 5
/** O local com o nono tem 9 dígitos; acima disso o `9` do começo é o DDD (91 a 99), e não o nono. */
const MAXIMO_DO_LOCAL_COM_O_NONO = 9
/** O nono tirado depois do DDD (`319942` → `31942`): de 6 a 11 dígitos (o teto é o celular). */
const MINIMO_DO_DDD_COM_O_NONO = 6
/**
 * O nono tirado depois de DDI e DDD (`553199` → `55319`): de 6 a 13 dígitos. O 6 é o primeiro
 * tamanho que tem o nono (`55` + `DD` + `9` + um dígito), e é ele que impede a conversa de sumir
 * enquanto se digita com DDI: `5531` e `55319` casam pelos dígitos crus, e daí em diante a variante.
 */
const MINIMO_DO_DDI_COM_O_NONO = 6
/** DDI, DDD e o celular com o nono: 13 dígitos. */
const MAXIMO_DO_DDI_COM_O_NONO = 13
/** A posição do nono quando o termo começa por DDI e DDD: `55` + `DD` + `9`. */
const INDICE_DO_NONO_COM_DDI = DDI_DO_BRASIL.length + INDICE_DO_NONO

/** O texto sem o caractere daquela posição. */
function semAPosicao(digitos: string, indice: number): string {
  return digitos.slice(0, indice) + digitos.slice(indice + 1)
}

/**
 * As formas sem o nono, para achar o termo digitado **com** ele contra o número que o WhatsApp
 * gravou **sem** ele — comum do DDD 31 em diante (`553194235059`). Três formas, cada uma com o seu
 * mínimo de dígitos (emenda de 21/10/2026, aprovada pelo Gustavo):
 *
 * - começando por `9`, de 5 a 9 dígitos: o pedaço local (`99423` → `9423`);
 * - `DD9…`, de 6 a 11: o terceiro dígito sai (`319942` → `31942`; `22997550997` → `2297550997`);
 * - `55DD9…`, de 6 a 13: o quinto dígito sai (`553199` → `55319`; `55319942` → `5531942`).
 *
 * Abaixo desses mínimos não há variante: `9942` continua sendo só `9942`, e nenhuma variante fica
 * com menos de 4 dígitos. **O teto de cada forma é o comprimento do número que ela descreve**
 * (revisão da emenda): sem ele, `(91) 98888-7777` (DDD 91, Norte) perderia o `9` do DDD e acharia o
 * contato de DDD 19 com o mesmo assinante. O preço, aceito, é que um pedaço de 5 dígitos começando
 * por `9` também vale como busca dos 4 seguintes, e que um pedaço local cujo terceiro dígito é `9`
 * (`33991234`) ou um DDD 55 digitado sem DDI também ganham variante: a forma do pedaço não diz se
 * ele começa pelo DDD.
 */
function semONono(digitos: string): string[] {
  const variantes: string[] = []
  const tamanho = digitos.length
  if (
    tamanho >= MINIMO_DO_LOCAL_COM_O_NONO &&
    tamanho <= MAXIMO_DO_LOCAL_COM_O_NONO &&
    digitos.startsWith('9')
  ) {
    variantes.push(digitos.slice(1))
  }
  if (
    tamanho >= MINIMO_DO_DDD_COM_O_NONO &&
    tamanho <= TAMANHO_BRASILEIRO_COM_O_NONO &&
    digitos[INDICE_DO_NONO] === '9'
  ) {
    variantes.push(semAPosicao(digitos, INDICE_DO_NONO))
  }
  if (
    tamanho >= MINIMO_DO_DDI_COM_O_NONO &&
    tamanho <= MAXIMO_DO_DDI_COM_O_NONO &&
    digitos.startsWith(DDI_DO_BRASIL) &&
    digitos[INDICE_DO_NONO_COM_DDI] === '9'
  ) {
    variantes.push(semAPosicao(digitos, INDICE_DO_NONO_COM_DDI))
  }
  return variantes
}

/**
 * O termo da busca preparado para casar telefone, ou `null` quando ele não tem cara de telefone.
 *
 * `null` quando há letra (`\p{L}`) ou menos de 4 dígitos ASCII. Marcas invisíveis do texto colado
 * (U+202A, U+202C), NBSP, U+202F e hífens Unicode não impedem: só letra e dígito contam.
 */
export function prepararTermo(termo: string): TermoPreparado | null {
  if (/\p{L}/u.test(termo)) {
    return null
  }
  const crua = digitosDe(termo)
  if (crua.length < MINIMO_DE_DIGITOS_NO_TERMO) {
    return null
  }
  const semZeros = semZerosDeDiscagem(crua)
  const candidatas = new Set([crua, semZeros, ...semONono(semZeros)])
  return { candidatas: [...candidatas], chave: chaveDoTelefone(semZeros) }
}

/**
 * Se o número da conversa casa com o termo preparado.
 *
 * Verdadeiro quando alguma candidata está **contida** nos dígitos do número, na chave dele ou
 * (chave de 10 dígitos) em `'55' + chave`; ou quando as duas chaves são iguais e nenhuma é nula.
 * As regras se completam:
 *
 * - os dígitos crus acham o pedaço igual ao gravado;
 * - a chave acha o termo sem DDI e sem o nono, inclusive durante a digitação;
 * - `'55' + chave` acha o termo parcial com DDI e sem o nono contra o número gravado com o nono;
 * - a chave igual acha o termo completo, com ou sem DDI e com ou sem o nono, contra o número
 *   gravado do outro jeito;
 * - a variante sem o `9` (`semONono`) acha o pedaço com o nono contra o número gravado sem ele,
 *   inclusive durante a digitação: o local a partir de 5 dígitos, com o DDD a partir de 6, com DDI
 *   e DDD a partir de 6.
 */
export function numeroCasa(numero: string, preparado: TermoPreparado | null): boolean {
  if (preparado === null) {
    return false
  }
  const digitos = digitosDe(numero)
  const chave = chaveDoTelefone(numero)
  const comDdi =
    chave !== null && chave.length === TAMANHO_BRASILEIRO_SEM_O_NONO ? DDI_DO_BRASIL + chave : null
  const contida = preparado.candidatas.some(
    (candidata) =>
      digitos.includes(candidata) ||
      (chave !== null && chave.includes(candidata)) ||
      (comDdi !== null && comDdi.includes(candidata)),
  )
  return contida || (chave !== null && preparado.chave !== null && chave === preparado.chave)
}
